import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { WeeklyHours } from '@communitly/shared';
import { useTranslation } from 'react-i18next';

export interface Service {
  id: string;
  name: string;
  description: string | null;
  category: string;
  categoryName?: string;
  images?: { id: string; url: string }[];
  imageUrl: string;
  companyLogo?: string;
  companyBannerUrl?: string;
  rating: number;
  reviewCount: number;
  price: number | string | 'Free';
  location: string;
  contactEmail: string;
  contactPhone: string;
  website?: string;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    county?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  socialLinks?: {
    facebook?: string | null;
    instagram?: string | null;
    tiktok?: string | null;
  };
  googleMapsUrl?: string | null;
  discount?: {
    id: string;
    amount: number;
    isPercentage: boolean;
    appliesToAll: boolean;
    type?: 'Paid' | 'Free';
    description?: string | null;
  } | null;
  is_active?: boolean;
  is_listed?: boolean;
  opening_hours?: WeeklyHours | null;
  isfeatured?: boolean;
  slug?: string;
}

interface UseServicesOptions {
  infinite?: boolean;
  pageSize?: number;
}

// Core service fetching function
const fetchServicesData = async ({
  currentCommunityId,
  categoryId,
  featured,
  currentLocale,
  pageParam = 0,
  pageSize = 10,
  infinite = false
}: {
  currentCommunityId: string;
  categoryId?: string;
  featured?: boolean;
  currentLocale: string;
  pageParam?: number;
  pageSize?: number;
  infinite?: boolean;
}) => {
  let query = supabase
    .from('businesses')
    .select(`
      id,
      name,
      slug,
      description,
      contact_email,
      contact_phone,
      website,
      company_logo,
      company_banner_url,
      address_line1,
      address_line2,
      city,
      county,
      postal_code,
      country,
      facebook_url,
      instagram_url,
      tiktok_url,
      google_maps_url,
      business_image_1_url,
      business_image_2_url,
      business_image_3_url,
      is_active,
      is_listed,
      isfeatured,
      opening_hours,
      categories!inner(
        id,
        translation_key,
        translation_defaults!inner(key, default_value)
      ),
      business_discounts!left(
        id,
        discount_amount,
        is_percentage,
        applies_to_all,
        type,
        description,
        is_active,
        status
      )
    `)
    .eq('community_id', currentCommunityId)
    .eq('is_listed', true)
    .order('created_at', { ascending: false });

  if (categoryId) {
    query = query.eq('category_id', categoryId);
  }

  if (featured) {
    query = query.eq('isfeatured', true);
  }

  // Add pagination for infinite scroll
  if (infinite) {
    query = query.range(pageParam, pageParam + pageSize - 1);
  }

  const { data: businessData, error } = await query;

  if (error) {
    console.error('Services query error:', error);
    throw error;
  }

  // Get business IDs for review calculation
  const businessIds = businessData?.map(business => business.id) || [];

  // Fetch published reviews for rating calculation
  const { data: reviewsData, error: reviewsError } = await supabase
    .from('reviews')
    .select('business_id, rating')
    .eq('status', 'Published')
    .in('business_id', businessIds);

  if (reviewsError) {
    console.error('Reviews query error:', reviewsError);
    throw reviewsError;
  }

  // Calculate ratings and review counts for each business
  const businessRatings = new Map();
  const businessReviewCounts = new Map();

  if (reviewsData) {
    reviewsData.forEach(review => {
      const businessId = review.business_id;
      
      if (!businessRatings.has(businessId)) {
        businessRatings.set(businessId, []);
        businessReviewCounts.set(businessId, 0);
      }
      
      businessRatings.get(businessId).push(review.rating);
      businessReviewCounts.set(businessId, businessReviewCounts.get(businessId) + 1);
    });
  }

  // Calculate average ratings
  const businessAverageRatings = new Map();
  businessRatings.forEach((ratings, businessId) => {
    const average = ratings.reduce((sum: number, rating: number) => sum + rating, 0) / ratings.length;
    businessAverageRatings.set(businessId, average);
  });

  // Fetch community-specific translations for categories if they exist
  const categoryTranslationKeys = businessData?.map((business) => business.categories?.translation_key).filter(Boolean) || [];
  
  const { data: categoryTranslations, error: categoryTranslationsError } = await supabase
    .from('community_translations')
    .select('translation_key, value')
    .eq('locale', currentLocale)
    .in('translation_key', categoryTranslationKeys);

  if (categoryTranslationsError) {
    console.error('Error fetching category translations:', categoryTranslationsError);
  }

  // Create a map of category translation overrides
  const categoryTranslationOverrides = new Map();
  if (categoryTranslations) {
    categoryTranslations.forEach(t => {
      categoryTranslationOverrides.set(t.translation_key, t.value);
    });
  }

  const services = businessData?.map((business) => {
    // Get the default category name from translation_defaults
    const defaultCategoryName = business.categories?.translation_defaults?.default_value || business.categories?.translation_key || 'Other';
    
    // Check if there's a community-specific translation override
    const translatedCategoryName = categoryTranslationOverrides.get(business.categories?.translation_key) || defaultCategoryName;

    // Get calculated rating and review count
    const calculatedRating = businessAverageRatings.get(business.id) || 0;
    const calculatedReviewCount = businessReviewCounts.get(business.id) || 0;

    return {
      id: business.id,
      name: business.name,
      slug: business.slug,
      description: business.description,
      category: business.categories?.translation_key || 'Other',
      categoryName: translatedCategoryName,
      imageUrl: business.business_image_1_url || business.company_banner_url || '/placeholder.svg',
      companyLogo: business.company_logo,
      companyBannerUrl: business.company_banner_url,
      rating: calculatedRating,
      reviewCount: calculatedReviewCount,
      price: 'Contact for pricing',
      location: [business.city, business.postal_code].filter(Boolean).join(', ') || 'Location not specified',
      contactEmail: business.contact_email,
      contactPhone: business.contact_phone,
      website: business.website,
      address: {
        line1: business.address_line1,
        line2: business.address_line2,
        city: business.city,
        county: business.county,
        postalCode: business.postal_code,
        country: business.country,
      },
      socialLinks: {
        facebook: business.facebook_url,
        instagram: business.instagram_url,
        tiktok: business.tiktok_url,
      },
      googleMapsUrl: business.google_maps_url,
      discount: business.business_discounts?.[0]?.is_active ? {
        id: business.business_discounts[0].id,
        amount: business.business_discounts[0].discount_amount,
        isPercentage: business.business_discounts[0].is_percentage,
        appliesToAll: business.business_discounts[0].applies_to_all,
        type: business.business_discounts[0].type,
        description: business.business_discounts[0].description,
      } : null,
      is_active: business.is_active,
      is_listed: business.is_listed,
      isfeatured: business.isfeatured,
      opening_hours: business.opening_hours as WeeklyHours | null,
    };
  }) || [];

  if (infinite) {
    return {
      services,
      nextCursor: businessData && businessData.length === pageSize ? pageParam + pageSize : undefined,
      hasMore: businessData && businessData.length === pageSize,
    };
  }

  return services;
};

const useServices = (categoryId?: string, featured?: boolean, options: UseServicesOptions = {}) => {
  const { infinite = false, pageSize = 10 } = options;
  const { currentCommunity } = useCommunity();
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;

  // Call both hooks always, but enable only one
  const infiniteQuery = useInfiniteQuery({
    queryKey: ['services-infinite', currentCommunity?.id, categoryId, featured, currentLocale],
    queryFn: ({ pageParam = 0 }) => fetchServicesData({
      currentCommunityId: currentCommunity?.id || '',
      categoryId,
      featured,
      currentLocale,
      pageParam,
      pageSize,
      infinite: true
    }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: infinite && !!currentCommunity?.id,
    initialPageParam: 0,
  });

  const regularQuery = useQuery({
    queryKey: ['services', currentCommunity?.id, categoryId, featured, currentLocale],
    queryFn: () => fetchServicesData({
      currentCommunityId: currentCommunity?.id || '',
      categoryId,
      featured,
      currentLocale,
      infinite: false
    }),
    enabled: !infinite && !!currentCommunity?.id
  });

  // Return the appropriate query result
  return infinite ? infiniteQuery : regularQuery;
};

// Named export for compatibility
export { useServices };

export default useServices;